import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, Search, MoreHorizontal, Send, Edit2, 
  XCircle, DollarSign, Loader2, Eye, RefreshCw, Trash2, Gift
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const EventOrdersPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    status: '',
    admin_notes: ''
  });
  
  // Refund form
  const [refundForm, setRefundForm] = useState({
    partial_amount: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, ordersRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getOrders(eventId),
      ]);
      setEvent(eventRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditForm({
      buyer_name: order.buyer_name || '',
      buyer_email: order.buyer_email || '',
      buyer_phone: order.buyer_phone || '',
      status: order.status || '',
      admin_notes: order.admin_notes || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      await eventsApi.updateOrder(eventId, selectedOrder.id, editForm);
      toast.success('Orden actualizada correctamente');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar orden');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendTickets = async (order) => {
    setActionLoading(true);
    try {
      await eventsApi.resendOrderTickets(eventId, order.id);
      toast.success(`Tickets reenviados a ${order.buyer_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar tickets');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRefund = (order) => {
    setSelectedOrder(order);
    setRefundForm({ partial_amount: '', reason: '' });
    setShowRefundModal(true);
  };

  const handleRefund = async (isPartial = false) => {
    setActionLoading(true);
    try {
      const partialAmount = isPartial && refundForm.partial_amount ? parseFloat(refundForm.partial_amount) : null;
      await eventsApi.refundOrder(eventId, selectedOrder.id, partialAmount, refundForm.reason);
      toast.success(isPartial ? 'Reembolso parcial registrado' : 'Reembolso completo registrado');
      setShowRefundModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar reembolso');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async (order) => {
    if (!confirm('¿Estás seguro de cancelar esta orden? Los tickets asociados serán invalidados.')) return;
    
    setActionLoading(true);
    try {
      await eventsApi.updateOrder(eventId, order.id, { status: 'cancelled' });
      toast.success('Orden cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar orden');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!confirm(`¿Eliminar esta orden permanentemente? Se restockearán ${(order.total_tickets || order.quantity) || 1} entrada(s) y se borrarán los tickets asociados.`)) return;
    
    setActionLoading(true);
    try {
      const res = await eventsApi.deleteOrder(eventId, order.id);
      toast.success(res.data.message || 'Orden eliminada y entradas restockeadas');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar orden');
    } finally {
      setActionLoading(false);
    }
  };


  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const filteredOrders = orders.filter(order => 
    order.buyer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validStatuses = ['completed', 'approved', 'paid', 'confirmed'];
  const totalRevenue = orders.filter(o => validStatuses.includes(o.status)).reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const totalTickets = orders.filter(o => validStatuses.includes(o.status)).reduce((sum, o) => sum + (o.quantity || 0) + (o.bonus_quantity || 0), 0);

  const promoLabel = (o) => (o?.promo_applied?.pay_x && o?.promo_applied?.get_y) ? `${o.promo_applied.get_y}x${o.promo_applied.pay_x}` : 'Promo';

  const getStatusBadge = (status) => {
    const configs = {
      completed: { label: 'Completado', class: 'bg-green-500/20 text-green-500' },
      approved: { label: 'Aprobado', class: 'bg-green-500/20 text-green-500' },
      paid: { label: 'Pagado', class: 'bg-green-500/20 text-green-500' },
      confirmed: { label: 'Confirmado', class: 'bg-green-500/20 text-green-500' },
      pending: { label: 'Pendiente', class: 'bg-yellow-500/20 text-yellow-500' },
      cancelled: { label: 'Cancelado', class: 'bg-red-500/20 text-red-500' },
      refunded: { label: 'Reembolsado', class: 'bg-purple-500/20 text-purple-500' },
      partially_refunded: { label: 'Reembolso Parcial', class: 'bg-purple-500/20 text-purple-500' },
    };
    const config = configs[status] || { label: status, class: 'bg-zinc-500/20 text-zinc-500' };
    return <span className={`px-2 py-1 text-xs font-bold uppercase ${config.class}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="h-96 bg-[#1A1A1A] rounded" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-orders-page">
          <EventSubNav eventTitle={event?.title} />

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-6 mb-8"
          >
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Total Órdenes</p>
              <p className="text-3xl font-black">{orders.length}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Tickets Vendidos</p>
              <p className="text-3xl font-black">{totalTickets}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Ingresos</p>
              <p className="text-3xl font-black">${totalRevenue.toLocaleString()}</p>
            </div>
          </motion.div>

          {/* Search and Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between mb-6"
          >
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input
                type="text"
                placeholder="Buscar por email, nombre o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-[#121212] border border-[#27272A] text-white placeholder-[#52525B] focus:border-white outline-none w-80"
                data-testid="orders-search-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="border-[#27272A]" onClick={fetchData}>
                <RefreshCw size={16} className="mr-2" />
                Actualizar
              </Button>
              <Button variant="outline" className="border-[#27272A]">
                <Download size={16} className="mr-2" />
                Exportar CSV
              </Button>
            </div>
          </motion.div>

          {/* Orders Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-dashboard overflow-hidden p-0"
          >
            {filteredOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-[#27272A]">
                    <tr>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">ID</th>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Comprador</th>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Cantidad</th>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Total</th>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Estado</th>
                      <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Fecha</th>
                      <th className="text-right py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A]">
                        <td className="py-4 px-4">
                          <code className="text-xs bg-[#1A1A1A] px-2 py-1">{order.id?.slice(0, 8)}...</code>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">{order.buyer_name || '-'}</p>
                            <p className="text-sm text-[#A1A1AA]">{order.buyer_email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 font-mono">
                          {(order.bonus_quantity || 0) > 0 ? (
                            <div>
                              <span className="font-bold">{order.total_tickets || order.quantity + order.bonus_quantity}</span>
                              <p className="text-[10px] text-emerald-400 font-sans flex items-center gap-1" data-testid={`admin-order-promo-${order.id?.slice(0, 8)}`}>
                                <Gift size={10} />
                                Promo {promoLabel(order)} · {order.quantity} paga{order.quantity > 1 ? 's' : ''} + {order.bonus_quantity} gratis
                              </p>
                            </div>
                          ) : (
                            order.quantity
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col">
                            {(order.discount_amount || 0) > 0 && (
                              <p className="text-[10px] text-zinc-500 line-through">${(order.subtotal || (order.unit_price * order.quantity) || 0).toLocaleString()}</p>
                            )}
                            <p className="font-mono font-bold">${order.total_amount?.toLocaleString()}</p>
                            {(order.discount_amount || 0) > 0 && (
                              <p className="text-[10px] text-amber-400" data-testid={`admin-order-discount-${order.id?.slice(0,8)}`}>
                                {order.discount?.code || order.discount_code ? `${order.discount?.code || order.discount_code}: ` : ''}−${(order.discount_amount || 0).toLocaleString()}
                              </p>
                            )}
                            {(order.discount_code || order.discount?.code) && (order.discount_amount || 0) === 0 && (
                              <code className="text-xs bg-green-500/20 text-green-500 px-1 mt-1">{order.discount_code || order.discount?.code}</code>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">{getStatusBadge(order.status)}</td>
                        <td className="py-4 px-4 text-[#A1A1AA] text-sm">
                          {order.created_at && format(new Date(order.created_at), 'dd/MM/yy HH:mm')}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`order-actions-${order.id?.slice(0, 8)}`}>
                                <MoreHorizontal size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A]">
                              <DropdownMenuItem onClick={() => handleViewDetails(order)}>
                                <Eye size={14} className="mr-2" />
                                Ver Detalles
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditOrder(order)}>
                                <Edit2 size={14} className="mr-2" />
                                Editar Orden
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResendTickets(order)}>
                                <Send size={14} className="mr-2" />
                                Reenviar Tickets
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#27272A]" />
                              <DropdownMenuItem onClick={() => handleOpenRefund(order)}>
                                <DollarSign size={14} className="mr-2" />
                                Reembolsar
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleCancelOrder(order)}
                                className="text-red-500 focus:text-red-500"
                              >
                                <XCircle size={14} className="mr-2" />
                                Cancelar Orden
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteOrder(order)}
                                className="text-red-500 focus:text-red-500"
                                data-testid={`delete-order-${order.id}`}
                              >
                                <Trash2 size={14} className="mr-2" />
                                Eliminar Orden
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-[#52525B] text-lg">No hay órdenes para este evento</p>
                <p className="text-[#52525B] text-sm mt-2">Las órdenes aparecerán aquí cuando los usuarios compren tickets</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Edit Order Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black uppercase">Editar Orden</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#52525B]">Nombre del Comprador</Label>
                <Input
                  value={editForm.buyer_name}
                  onChange={(e) => setEditForm({ ...editForm, buyer_name: e.target.value })}
                  className="bg-transparent border-[#27272A]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#52525B]">Email</Label>
                <Input
                  type="email"
                  value={editForm.buyer_email}
                  onChange={(e) => setEditForm({ ...editForm, buyer_email: e.target.value })}
                  className="bg-transparent border-[#27272A]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#52525B]">Teléfono</Label>
                <Input
                  value={editForm.buyer_phone}
                  onChange={(e) => setEditForm({ ...editForm, buyer_phone: e.target.value })}
                  className="bg-transparent border-[#27272A]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#52525B]">Estado</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-transparent border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="completed">Completado</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                    <SelectItem value="refunded">Reembolsado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#52525B]">Notas del Admin</Label>
                <Textarea
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                  placeholder="Notas internas sobre esta orden..."
                  className="bg-transparent border-[#27272A]"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[#27272A]">
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={actionLoading} className="bg-white text-black hover:bg-[#E5E5E5]">
                {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Refund Modal */}
        <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black uppercase">Reembolsar Orden</DialogTitle>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-sm text-[#A1A1AA]">Monto Total de la Orden</p>
                  <p className="text-2xl font-black">${selectedOrder.total_amount?.toLocaleString()}</p>
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Monto a Reembolsar (dejar vacío para reembolso total)
                  </Label>
                  <Input
                    type="number"
                    value={refundForm.partial_amount}
                    onChange={(e) => setRefundForm({ ...refundForm, partial_amount: e.target.value })}
                    placeholder={`Máximo: $${selectedOrder.total_amount}`}
                    max={selectedOrder.total_amount}
                    className="bg-transparent border-[#27272A]"
                  />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">Motivo del Reembolso</Label>
                  <Textarea
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                    placeholder="Describa el motivo del reembolso..."
                    className="bg-transparent border-[#27272A]"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowRefundModal(false)} className="border-[#27272A]">
                Cancelar
              </Button>
              {refundForm.partial_amount ? (
                <Button 
                  onClick={() => handleRefund(true)} 
                  disabled={actionLoading}
                  className="bg-purple-600 text-white hover:bg-purple-700"
                >
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Reembolso Parcial (${refundForm.partial_amount})
                </Button>
              ) : (
                <Button 
                  onClick={() => handleRefund(false)} 
                  disabled={actionLoading}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Reembolso Total
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Order Details Modal */}
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="bg-[#121212] border-[#27272A] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-black uppercase">Detalles de la Orden</DialogTitle>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">ID de Orden</p>
                    <code className="text-sm">{selectedOrder.id}</code>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">Estado</p>
                    {getStatusBadge(selectedOrder.status)}
                  </div>
                </div>
                
                <div className="p-4 bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-xs text-[#A1A1AA] mb-2">Datos del Comprador</p>
                  <p className="font-medium">{selectedOrder.buyer_name || '-'}</p>
                  <p className="text-sm text-[#A1A1AA]">{selectedOrder.buyer_email}</p>
                  <p className="text-sm text-[#A1A1AA]">{selectedOrder.buyer_phone || '-'}</p>
                  {selectedOrder.buyer_dni && (
                    <p className="text-sm text-[#A1A1AA]">DNI: {selectedOrder.buyer_dni}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">Cantidad</p>
                    <p className="text-xl font-black">{selectedOrder.total_tickets || selectedOrder.quantity}</p>
                    {(selectedOrder.bonus_quantity || 0) > 0 && (
                      <p className="text-[10px] text-emerald-400" data-testid="admin-order-detail-promo-qty">
                        {selectedOrder.quantity} paga{selectedOrder.quantity > 1 ? 's' : ''} + {selectedOrder.bonus_quantity} gratis (Promo {promoLabel(selectedOrder)})
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">Total Pagado</p>
                    <p className="text-xl font-black">${selectedOrder.total_amount?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs text-[#A1A1AA]">Descuento</p>
                    <p className="text-xl font-black">{selectedOrder.discount?.code || selectedOrder.discount_code || '-'}</p>
                  </div>
                </div>
                
                {/* Financial breakdown */}
                <div className="p-4 bg-[#0A0A0A] border border-[#D4AF37]/40">
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest mb-3 font-bold">Desglose Financiero</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Subtotal ({selectedOrder.quantity} × ${(selectedOrder.unit_price || 0).toLocaleString()})</span>
                      <span className="font-mono">${(selectedOrder.subtotal || (selectedOrder.unit_price * selectedOrder.quantity) || 0).toLocaleString()}</span>
                    </div>
                    {(selectedOrder.bonus_quantity || 0) > 0 && (
                      <div className="flex justify-between text-emerald-400" data-testid="admin-order-detail-promo">
                        <span>Promo {promoLabel(selectedOrder)} · {selectedOrder.bonus_quantity} entrada{selectedOrder.bonus_quantity > 1 ? 's' : ''} de regalo{(selectedOrder.promo_applied?.amount_saved || 0) > 0 ? ` (ahorró $${selectedOrder.promo_applied.amount_saved.toLocaleString()})` : ''}</span>
                        <span className="font-mono">GRATIS</span>
                      </div>
                    )}
                    {(selectedOrder.service_fee || 0) > 0 && (
                      <div className="flex justify-between text-[#71717A]">
                        <span>+ Cargo por servicio (plataforma)</span>
                        <span className="font-mono">+${(selectedOrder.service_fee || 0).toLocaleString()}</span>
                      </div>
                    )}
                    {(selectedOrder.discount_amount || 0) > 0 && (
                      <div className="flex justify-between text-amber-400" data-testid="admin-order-detail-discount">
                        <span>− Descuento ({selectedOrder.discount?.code || selectedOrder.discount_code})</span>
                        <span className="font-mono">−${(selectedOrder.discount_amount || 0).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-[#27272A]">
                      <span className="font-semibold">= Total pagado por el cliente</span>
                      <span className="font-mono font-bold">${(selectedOrder.total_amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-[#27272A] text-emerald-400">
                      <span className="font-semibold">Ingreso neto productor</span>
                      <span className="font-mono font-bold" data-testid="admin-order-detail-net">
                        ${Math.max(0, (selectedOrder.subtotal || (selectedOrder.unit_price * selectedOrder.quantity) || 0) - (selectedOrder.discount_amount || 0)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {(selectedOrder.discount_amount || 0) > 0 && (
                    <p className="text-[10px] text-[#71717A] mt-3 italic">
                      El descuento se resta del ingreso del productor (no del cargo por servicio).
                    </p>
                  )}
                </div>
                
                {selectedOrder.admin_notes && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-xs text-yellow-500 mb-1">Notas del Admin</p>
                    <p className="text-sm">{selectedOrder.admin_notes}</p>
                  </div>
                )}
                
                {selectedOrder.refund_amount && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30">
                    <p className="text-xs text-purple-500 mb-1">Reembolso</p>
                    <p className="font-bold">${selectedOrder.refund_amount?.toLocaleString()}</p>
                    {selectedOrder.refund_reason && (
                      <p className="text-sm text-[#A1A1AA] mt-1">{selectedOrder.refund_reason}</p>
                    )}
                  </div>
                )}
                
                <div className="text-sm text-[#52525B]">
                  <p>Creado: {selectedOrder.created_at && format(new Date(selectedOrder.created_at), 'dd/MM/yyyy HH:mm')}</p>
                  {selectedOrder.updated_at && (
                    <p>Actualizado: {format(new Date(selectedOrder.updated_at), 'dd/MM/yyyy HH:mm')} por {selectedOrder.updated_by}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default EventOrdersPage;
